import React, { useMemo } from "react";
import TanStackTable from "./TanStackTable";


function convertColumns(columns) {
  return columns.map((col, index) => {

    let className = "";
    if (col.center) className += " text-center";
    if (col.allowOverflow) className += " overflow-visible";
    

    if (col.maxWidth && !col.allowOverflow && !col.cell) {
      className += " truncate";
    }
    
    if (col.meta?.className) className += " " + col.meta.className;
    

    if (col.hide === 'lg') className += " hidden lg:table-cell";

    const meta = { ...col.meta };

    if (col.maxWidth) {
      meta.style = { ...meta.style, maxWidth: col.maxWidth };
    }
    
    if (className.trim()) meta.className = className.trim();


    const tanStackCol = {
      id: `col_${index}_${col.sortSelector || col.name}`,
      header: col.name,
      enableSorting: col.sortable || false,
      meta
    };    
    if (col.cell) {
      tanStackCol.cell = col.cell;
    } else if (col.maxWidth && !col.allowOverflow) {
      tanStackCol.cell = ({ row }) => {
        const value = col.selector ? 
          (typeof col.selector === 'function' ? col.selector(row.original) : row.original[col.selector]) : 
          '';
        return (
          <div 
            className="truncate" 
            title={value} 
            style={{ maxWidth: col.maxWidth }}
          >
            {value}
          </div>
        );
      };
    }

    if (col.selector) {
      if (typeof col.selector === 'string') {
        tanStackCol.accessorFn = (row) => row[col.selector];
      } else if (typeof col.selector === 'function') {
        tanStackCol.accessorFn = col.selector;
      }
    } else if (col.sortSelector) {
      tanStackCol.accessorFn = (row) => row[col.sortSelector];
    }

    return tanStackCol;
  });
}

const UniversalDataTable = (props) => {
  const columns = useMemo(() => convertColumns(props.columns), [props.columns]);

  const enableRowSelection = Boolean(props.selectableRows);
  let pageIndex, pageSize, totalRecords, pageCount;
  

  const currentPageNum = useMemo(() => {
    if (props.query?.page_num) {
      return props.query.page_num;
    }
    
    if (props.currentPage) {
      return props.currentPage;
    }
    if (props.initialPage) {
      return props.initialPage;
    }
    return 1;
  }, [props.query?.page_num, props.currentPage, props.initialPage]);
  

  pageSize = props.query?.page_size || props.pageSize || 10;
  

  totalRecords = props.query?.records || props.totalRecords || props.data?.length || 0;

  pageIndex = currentPageNum - 1;
  
  const currentPageItems = props.data?.length || 0;
  
  if (currentPageItems >= totalRecords) {
    pageCount = 1;
  } else {
    pageCount = totalRecords && pageSize ? Math.ceil(totalRecords / pageSize) : 1;
  }
  

  const startIndex = (pageIndex * pageSize) + 1;
  const endIndex = Math.min((pageIndex * pageSize) + currentPageItems, totalRecords);
  

  const correctedQuery = {
    ...props.query,
    page_num: currentPageNum,
    page_size: pageSize,
    records: totalRecords,
    startIndex: startIndex - 1, 
    endIndex: endIndex - 1,     
  };
  let selectedRowIds = props.selectedRowIds || {};
  if (Array.isArray(props.selectedRows)) {
    selectedRowIds = {};
    props.selectedRows.forEach(row => {
      if (row.id) selectedRowIds[row.id] = true;
    });
  }

  const multipleFunction = props.multipleDeleteFunction || props.multipleFunction;
  const multipleFunctionLabel = props.multipleFunctionLabel || (props.multipleDeleteFunction ? "Eliminar seleccionados" : "");
  const multipleFunctionIcon = props.multipleFunctionIcon || (props.multipleDeleteFunction ? "trash" : "");


  const extraTableFunctions = props.extraTableFunctions || []; 
  const handlePaginationChange = (pagination) => {
    let newPageIndex;
    if (typeof pagination === 'object' && pagination.pageIndex !== undefined) {
      newPageIndex = pagination.pageIndex;
    } else if (typeof pagination === 'number') {
      newPageIndex = pagination;
    } else {
      console.error('❌ Pagination object inválido:', pagination);
      return;
    }
    
    const newPageNumber = newPageIndex + 1; 
    
    if (props.onPageChange) {
      props.onPageChange(newPageNumber);
    }
    if (props.onPaginationChange) {
      props.onPaginationChange(pagination);
    }
    if (props.getDataFunction) {
      props.getDataFunction(newPageNumber);
    }
  };  
  const handleSortingChange = (sorting) => {
    if (!sorting || sorting.length === 0) {
      if (props.customSort) {
        const currentPage = pageIndex + 1;
        props.customSort(null, null, currentPage);
      }
      return;
    }
    
    if (props.customSort && Array.isArray(sorting) && sorting.length > 0) {
      const sort = sorting[0];
      if (sort && sort.id) {
        const colId = sort.id;
        const colIndex = parseInt(colId.split('_')[1]);
        const originalColumn = props.columns[colIndex];
        const field = originalColumn?.sortSelector || sort.id; 
        const order = sort.desc ? 'desc' : 'asc';
        const currentPage = pageIndex + 1;
        props.customSort(field, order, currentPage);
      }
    }
  };

  return (
    <TanStackTable
      data={props.data}
      columns={columns}
      loading={props.loading}
      pageCount={pageCount}
      pageIndex={pageIndex}
      pageSize={pageSize}
      onPaginationChange={handlePaginationChange}
      onRowSelectionChange={props.onRowSelectionChange}
      selectedRowIds={selectedRowIds}
      extraTableFunctions={extraTableFunctions}
      noDataComponent={props.noDataComponent || "No se encontraron registros"}
      getDataFunction={props.getDataFunction}
      screenWidth={props.screenWidth}
      multipleFunction={multipleFunction}
      multipleFunctionLabel={multipleFunctionLabel}
      multipleFunctionIcon={multipleFunctionIcon}
      conditionalRowStyles={props.conditionalRowStyles}
      customLoaderComponent={props.customLoaderComponent}
      onRowClicked={props.onRowClicked}
      selectableRowDisabled={props.selectableRowDisabled}      initialPage={props.initialPage}
      query={correctedQuery}
      pagination={props.pagination !== false}
      enableRowSelection={enableRowSelection}
      onSortingChange={handleSortingChange}
      expandableRows={props.expandableRows}
      expandableRowsComponent={props.expandableRowsComponent}
      onRowExpand={props.onRowExpand}
    />
  );
};

export default UniversalDataTable;